import OgImage from '@propertypal/shared/src/resources/images/careers/open-graph1.jpg';
import axios, { STRAPI_URL } from '@propertypal/shared/src/services/axios.strapi';
import dayjs from '@propertypal/shared/src/services/dayjs';
import { IJob, IPropertyPalCareersPage } from '@propertypal/shared/src/types/strapi';
import { StrapiResponse } from '@propertypal/shared/src/types/strapi/generic';
import CareersHero from '@propertypal/web-ui/src/careers/CareersHero';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import RichText from '@propertypal/web-ui/src/rich-text/RichText';
import { MidHeading, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { useTheme } from 'styled-components';
import HowToApplySection from '../../components/careers/HowToApplySection';
import NoteForAgencies from '../../components/careers/NoteForAgencies';
import generateMetaTags from '../../components/layout/MetaTags';

interface CareerDetailsProps {
  job: IJob | null;
  careersData: IPropertyPalCareersPage | null;
}

const metaData = {
  title: 'Careers - PropertyPal',
  description: 'See The Current Job Opportunities At PropertyPal, The Leading Local Property Portal.',
  image: {
    url: OgImage.src,
    width: 1200,
    height: 650,
  },
};

export const getServerSideProps: GetServerSideProps<CareerDetailsProps> = async ({ query }) => {
  const { slug } = query;

  const jobsResult = await axios<StrapiResponse<IJob[]>>({
    method: 'GET',
    url: `${STRAPI_URL}/jobs`,
    params: { populate: 'deep', 'filters[slug][$eq]': slug },
  });

  const careersResult = await axios<StrapiResponse<IPropertyPalCareersPage>>({
    method: 'GET',
    url: `${STRAPI_URL}/property-pal-careers-page`,
    params: { populate: 'deep' },
  });

  return {
    props: {
      job: jobsResult.data.data?.[0] ?? null,
      careersData: careersResult.data.data,
    },
  };
};

const CareerDetailsPage: React.FC<CareerDetailsProps> = ({ job, careersData }) => {
  const router = useRouter();
  const { slug } = router.query;

  const theme = useTheme();

  return (
    <>
      <Head>{generateMetaTags({ ...metaData, url: `/careers/${slug}` })}</Head>

      <CareersHero img={careersData?.attributes?.hero?.image?.data?.attributes?.url || ''} />

      <PageWrapper>
        <div style={{ padding: '60px 0' }}>
          {job ? (
            <>
              <MidHeading data-testid="job-title" style={{ marginBottom: '5px' }}>
                {job?.attributes?.title}
              </MidHeading>
              <Text color={theme.primary} style={{ marginBottom: '22px' }}>
                Published: {dayjs(job?.attributes?.publishedAt).format('Do MMMM YYYY')}
              </Text>
              <RichText html={job?.attributes?.description} />
              <Text color={theme.primary} style={{ marginTop: '32px' }}>
                Closing Date: {job?.attributes?.deadline && dayjs(job?.attributes?.deadline).format('Do MMMM YYYY')}
              </Text>
            </>
          ) : (
            <SubHeading data-testid="job-not-found" style={{ marginBottom: '5px', textAlign: 'center' }}>
              Job Not Found
            </SubHeading>
          )}
        </div>
      </PageWrapper>
      <HowToApplySection howToApplySection={careersData?.attributes?.howToApply} />
      <NoteForAgencies />
    </>
  );
};

export default CareerDetailsPage;
